import { zodToEffectSchema } from "@redotech/effect-api-shared-common/zod";
import { ConfirmAddToCart } from "@redotech/redo-model/agents/support-agents/confirm-add-to-cart";
import { ConfirmCancelOrderSchema } from "@redotech/redo-model/agents/support-agents/confirm-cancel-order";
import { ConfirmCompareProductsSchema } from "@redotech/redo-model/agents/support-agents/confirm-compare-products";
import { ConfirmContactSupport } from "@redotech/redo-model/agents/support-agents/confirm-contact-support";
import { ConfirmCreateBundleSchema } from "@redotech/redo-model/agents/support-agents/confirm-create-bundle";
import { ConfirmDiscountBubble } from "@redotech/redo-model/agents/support-agents/confirm-discount-bubble";
import { ConfirmProductVisualizationSchema } from "@redotech/redo-model/agents/support-agents/confirm-product-visualization";
import { ConfirmResendReturnLabelSchema } from "@redotech/redo-model/agents/support-agents/confirm-resend-return-label";
import { SubscribeBackInStockConfirmationSchema } from "@redotech/redo-model/agents/support-agents/confirm-subscribe-to-back-in-stock";
import { ConfirmUpdateOrderSchema } from "@redotech/redo-model/agents/support-agents/confirm-update-order";
import { ShippingAddressToUpdateSchema } from "@redotech/redo-model/agents/support-agents/confirm-update-order-shipping-address";
import {
  GetRelevantProductsOutputParser,
  ProductDetailsParser,
} from "@redotech/redo-model/concierge-conversation/product-details-db-parser";
import {
  ComponentConfirmationState,
  ContentType,
  OrderAvailableToTrack,
} from "@redotech/redo-model/conversation";
import { BackInStockNotificationMessageContentSchema } from "@redotech/redo-model/conversation/message-parsers/back-in-stock-notification-message-parser";
import { CustomerSignInStructuredContentSchema } from "@redotech/redo-model/conversation/message-parsers/customer-sign-in-message-parser";
import { FaqLinkDataSchema } from "@redotech/redo-model/conversation/message-parsers/faq-link-message-parser";
import { TrackOrderUiStructuredMessageSchema } from "@redotech/redo-model/conversation/message-parsers/track-order-message-parser";
import { WishlistStructuredContentSchema } from "@redotech/redo-model/conversation/message-parsers/wishlist-message-parser";
import { Schema } from "effect";
import { MessageId } from "../../../../ids/definitions";

/**
 * Shared transport fields present on every redoChat message variant.
 * Factored out so each variant in the discriminated union picks them up
 * without repetition.
 *
 * - `content`: plain-text fallback stored on the parent `message` row.
 * - `confirmationState`: PENDING / CONFIRMED / REJECTED for tool-call UI
 *   messages; `null` when the variant has no confirmation semantics.
 * - `pendingToolCallId`: set while a tool-call confirmation is
 *   outstanding; the widget echoes it back on confirm/reject so the
 *   server can resolve the matching LLM tool call. `null` otherwise.
 * - `inReplyToMessageId`: optional thread-level reply link.
 */
const RedoChatMessageBaseFields = {
  content: Schema.String,
  confirmationState: Schema.NullOr(Schema.Enums(ComponentConfirmationState)),
  pendingToolCallId: Schema.NullOr(Schema.String),
  inReplyToMessageId: Schema.optional(MessageId),
};

/**
 * Canonical discriminated union for every redoChat widget message the AI
 * agent can emit. Keyed by `contentType`; each variant pairs a v1
 * `ContentType` with its typed `structuredContent` payload
 * (ProductDisplay → `{ productsToDisplay }`, CancelOrder →
 * `ConfirmCancelOrderSchema`, UpdateAddress → address form state, etc.)
 * plus the shared transport fields above.
 *
 * Payload shapes come straight from v1 Zod schemas (via
 * `zodToEffectSchema`) so the contract stays in sync with the widget
 * renderers without duplicating structure definitions.
 *
 * Used as the drafter input for structured replies, as inline channel
 * data for the outbound Temporal workflow, and (going forward) anywhere
 * else a typed redoChat message needs to flow.
 */
export const RedoChatMessage = Schema.Union(
  Schema.Struct({
    contentType: Schema.Literal(ContentType.TEXT),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.TOOL_CONFIRMATION_PROMPT),
    structuredContent: Schema.String,
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.TOOL_CALL_PRODUCT_INFO),
    structuredContent: zodToEffectSchema(ProductDetailsParser),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(
      ContentType.SUBSCRIBE_TO_BACK_IN_STOCK_NOTIFICATION,
    ),
    structuredContent: Schema.Struct({
      subscriptionsResult: Schema.Array(
        zodToEffectSchema(BackInStockNotificationMessageContentSchema),
      ),
    }),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.EMAIL_INPUT),
    structuredContent: Schema.Struct({
      customerEmail: Schema.Null,
      customerInputContent: Schema.Literal(true),
    }),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.TOOL_CALL_RELEVANT_PRODUCTS),
    structuredContent: zodToEffectSchema(GetRelevantProductsOutputParser),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.TOOL_CALL_TRACK_ORDER_AVAILABLE),
    structuredContent: Schema.Enums(OrderAvailableToTrack),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.CART_ITEMS),
    structuredContent: zodToEffectSchema(ConfirmAddToCart),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(
      ContentType.SUBSCRIBE_TO_BACK_IN_STOCK_NOTIFICATION_UI,
    ),
    structuredContent: zodToEffectSchema(
      SubscribeBackInStockConfirmationSchema,
    ),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.CONTACT_SUPPORT_UI),
    structuredContent: zodToEffectSchema(ConfirmContactSupport),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.RESEND_RETURN_LABEL_UI),
    structuredContent: zodToEffectSchema(ConfirmResendReturnLabelSchema),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.UPDATE_ADDRESS),
    structuredContent: zodToEffectSchema(ShippingAddressToUpdateSchema),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.DISCOUNT_CODE),
    structuredContent: zodToEffectSchema(ConfirmDiscountBubble),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.PRODUCT_DISPLAY),
    structuredContent: Schema.Struct({
      productsToDisplay: Schema.Array(
        Schema.Struct({ productId: Schema.String }),
      ),
    }),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.PRODUCT_COMPARISON),
    structuredContent: zodToEffectSchema(ConfirmCompareProductsSchema),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.PRODUCT_VISUALIZATION_UPLOAD_UI),
    structuredContent: zodToEffectSchema(ConfirmProductVisualizationSchema),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.PRODUCT_VISUALIZATION_RESULT),
    structuredContent: Schema.Struct({
      visualizationUrl: Schema.String,
      productId: Schema.optional(Schema.String),
      variantId: Schema.optional(Schema.String),
    }),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.DISPLAY_ORDER_TRACKING_INFO),
    structuredContent: Schema.Struct({
      orderId: Schema.String,
      email: Schema.String,
    }),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.TRACK_ORDER_UI),
    structuredContent: zodToEffectSchema(TrackOrderUiStructuredMessageSchema),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.UPDATE_ORDER),
    structuredContent: zodToEffectSchema(ConfirmUpdateOrderSchema),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.CREATE_BUNDLE),
    structuredContent: zodToEffectSchema(ConfirmCreateBundleSchema),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.FAQ_LINK),
    structuredContent: zodToEffectSchema(FaqLinkDataSchema),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.TOOL_CALL),
    structuredContent: Schema.Array(
      Schema.Struct({
        id: Schema.String,
        type: Schema.Literal("function"),
        function: Schema.Struct({
          name: Schema.String,
          arguments: Schema.String,
        }),
      }),
    ),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.TOOL_CALL_ERROR),
    structuredContent: Schema.Struct({ error: Schema.String }),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.CANCEL_ORDER),
    structuredContent: zodToEffectSchema(ConfirmCancelOrderSchema.required()),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.WISHLIST_DISPLAY),
    structuredContent: zodToEffectSchema(WishlistStructuredContentSchema),
    ...RedoChatMessageBaseFields,
  }),
  Schema.Struct({
    contentType: Schema.Literal(ContentType.CUSTOMER_SIGN_IN_UI),
    structuredContent: zodToEffectSchema(CustomerSignInStructuredContentSchema),
    ...RedoChatMessageBaseFields,
  }),
);
export type RedoChatMessage = typeof RedoChatMessage.Type;
